import {
  RichiestaPreventivoCreateDto,
  RichiestaPreventivoCreateResponseDto,
  useCreateRichiestaPreventivo,
  useUpdateVuoleEssereRicontattato,
  RichiestaPreventivoTipo,
} from "@/api";
import Box from "@/elements/Box";
import CoverLayout from "@/elements/CoverLayout";
import Typography from "@/elements/Typography";
import { Container } from "@mui/material";
import { useState } from "react";
import { SubmitHandler, useForm } from "react-hook-form";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import { LanguageSelector } from "./LanguageSelector";
import { ProgressBar } from "./ProgressBar";
import { Step0_LanguageSelection } from "./Step0_LanguageSelection";
import { Step1_TipoRichiesta } from "./Step1_TipoRichiesta";
import { Step2_TipoFluido } from "./Step2_TipoFluido";
import { Step3_Dettagli } from "./Step3_Dettagli";
import { Step4_DatiAzienda } from "./Step4_DatiAzienda";
import { ResultsView } from "./ResultsView";
import { BottomNavigation } from "./BottomNavigation";

type FormData = RichiestaPreventivoCreateDto;

function RichiestaPreventivo() {
  const { t, i18n } = useTranslation();
  const isDebugMode = new URLSearchParams(window.location.search).has("debug");
  const [activeStep, setActiveStep] = useState(0);
  const [prevStep, setPrevStep] = useState(0);
  const [result, setResult] =
    useState<RichiestaPreventivoCreateResponseDto | null>(null);
  const [showContactRequest, setShowContactRequest] = useState(false);
  const [wantsContact, setWantsContact] = useState(false);

  const { mutateAsync: createRichiesta, isLoading: isCreating } =
    useCreateRichiestaPreventivo();
  const { mutateAsync: updateContact, isLoading: isUpdatingContact } =
    useUpdateVuoleEssereRicontattato();

  const {
    handleSubmit,
    formState: { errors },
    control,
    register,
    watch,
    reset,
    trigger,
  } = useForm<FormData>({
    defaultValues: {
      nomeAzienda: isDebugMode ? "Azienda Demo SRL" : "",
      nomeRichiedente: isDebugMode ? "Mario Rossi" : "",
      posizione: isDebugMode ? "Responsabile Manutenzione" : "",
      email: isDebugMode ? "mario.rossi@demo.com" : "",
      numeroTelefono: isDebugMode ? "+39 02 1234567" : "",
      tipo: RichiestaPreventivoTipo.lubrifica,
      vuoleEssereRicontattato: false,
      quantitaOlioLubrifica: isDebugMode ? 500 : undefined,
      prezzoOlioLubrifica: isDebugMode ? 3.5 : undefined,
      frequenzaAnniDiCambioOlio: isDebugMode ? 1 : undefined,
      numeroPompeCambiateAnno: isDebugMode ? 3 : undefined,
      numeroValvoleCambiateAnno: isDebugMode ? 10 : undefined,
      oreFermoMacchinaAnno: isDebugMode ? 50 : undefined,
      costoOrarioFermoMacchina: isDebugMode ? 100 : undefined,
      costoUnitarioPompa: isDebugMode ? 500 : undefined,
      costoUnitarioValvola: isDebugMode ? 200 : undefined,
      frequenzaAnniDiCambioRefrigerante: isDebugMode ? 2 : undefined,
      quantitaOlioLavorazioni: isDebugMode ? 200 : undefined,
      concentrazioneRifrattometria: isDebugMode ? 8 : undefined,
      costoLitroOlio: isDebugMode ? 5 : undefined,
      spesaMediaAnnuaUtensili: isDebugMode ? 10000 : undefined,
      numeroPompeAltaPressioneLavorazioni: isDebugMode ? 8 : undefined,
      costoUnitarioPompaAltaPressione: isDebugMode ? 1000 : undefined,
    },
  });

  const tipoRichiesta = watch("tipo");
  const tipoOlioLavorazioni = watch("tipoOlioLavorazioni");

  // Watch step 1 fields for validation
  const nomeAzienda = watch("nomeAzienda");
  const nomeRichiedente = watch("nomeRichiedente");
  const posizione = watch("posizione");
  const email = watch("email");
  const numeroTelefono = watch("numeroTelefono");

  // Watch step 3/4 fields (lubrifica)
  const tipoOlioLubrifica = watch("tipoOlioLubrifica");
  const frequenzaAnniDiCambioOlio = watch("frequenzaAnniDiCambioOlio");
  const quantitaOlioLubrifica = watch("quantitaOlioLubrifica");
  const prezzoOlioLubrifica = watch("prezzoOlioLubrifica");

  // Watch step 3/4 fields (lavorazioni)
  const quantitaOlioLavorazioni = watch("quantitaOlioLavorazioni");
  const frequenzaAnniDiCambioRefrigerante = watch(
    "frequenzaAnniDiCambioRefrigerante",
  );
  const costoLitroOlio = watch("costoLitroOlio");
  const spesaMediaAnnuaUtensili = watch("spesaMediaAnnuaUtensili");

  const isStep1Valid =
    !!nomeAzienda &&
    !!nomeRichiedente &&
    !!posizione &&
    !!email &&
    !!numeroTelefono;

  const isValidNumber = (v: any) =>
    v !== undefined && v !== null && v !== "" && !isNaN(Number(v));

  const isStepFinalValid =
    tipoRichiesta === RichiestaPreventivoTipo.lubrifica
      ? !!tipoOlioLubrifica &&
        isValidNumber(frequenzaAnniDiCambioOlio) &&
        isValidNumber(quantitaOlioLubrifica) &&
        isValidNumber(prezzoOlioLubrifica)
      : !!tipoOlioLavorazioni &&
        isValidNumber(quantitaOlioLavorazioni) &&
        isValidNumber(frequenzaAnniDiCambioRefrigerante) &&
        isValidNumber(costoLitroOlio) &&
        isValidNumber(spesaMediaAnnuaUtensili);

  const handleNext = async () => {
    let fieldsToValidate: (keyof FormData)[] = [];

    if (activeStep === 1) {
      fieldsToValidate = ["tipo"];
    } else if (
      activeStep === 2 &&
      tipoRichiesta === RichiestaPreventivoTipo.lavorazioniMeccaniche
    ) {
      fieldsToValidate = ["tipoOlioLavorazioni"];
    } else if (activeStep === 4) {
      fieldsToValidate = [
        "nomeAzienda",
        "nomeRichiedente",
        "posizione",
        "email",
        "numeroTelefono",
      ];
    }

    const isValid = await trigger(fieldsToValidate);
    if (isValid) {
      setPrevStep(activeStep);
      // Se siamo al tipo richiesta e scegliamo lubrifica, saltiamo il tipo fluido e andiamo ai dettagli
      if (
        activeStep === 1 &&
        tipoRichiesta === RichiestaPreventivoTipo.lubrifica
      ) {
        setActiveStep(3);
      } else {
        setActiveStep((prev) => prev + 1);
      }
    }
  };

  const handleBack = () => {
    setPrevStep(activeStep);
    // Se siamo ai dettagli e la richiesta è lubrifica, torniamo alla scelta tipo richiesta saltando tipo fluido
    if (
      activeStep === 3 &&
      tipoRichiesta === RichiestaPreventivoTipo.lubrifica
    ) {
      setActiveStep(1);
    } else {
      setActiveStep((prev) => prev - 1);
    }
  };

  const onSubmit: SubmitHandler<FormData> = async (data) => {
    try {
      if (data.tipo === RichiestaPreventivoTipo.lubrifica) {
        // Lubrifica: azzera i campi esclusivi di Lavorazioni
        data.quantitaOlioLavorazioni = null;
        data.frequenzaAnniDiCambioRefrigerante = null;
        data.costoLitroOlio = null;
        data.spesaMediaAnnuaUtensili = null;
        data.concentrazioneRifrattometria = null;
        data.numeroPompeAltaPressioneLavorazioni = null;
        data.costoUnitarioPompaAltaPressione = null;
      } else {
        // Lavorazioni: azzera i campi esclusivi di Lubrifica
        data.quantitaOlioLubrifica = null;
        data.prezzoOlioLubrifica = null;
        data.frequenzaAnniDiCambioOlio = null;
        data.numeroPompeCambiateAnno = null;
        data.numeroValvoleCambiateAnno = null;
        data.oreFermoMacchinaAnno = null;
        data.costoOrarioFermoMacchina = null;
        data.costoUnitarioPompa = null;
        data.costoUnitarioValvola = null;
      }
      const response = await createRichiesta({
        data: { ...data, lingua: i18n.language },
      });
      setResult(response.data);
      setShowContactRequest(true);
      toast.success(t("richiestaPreventivo.results.toastSuccess"));
    } catch (error) {
      toast.error(t("richiestaPreventivo.results.toastError"));
      console.error(error);
    }
  };

  const handleContactSubmit = async () => {
    if (!result?.tokenRecupero) return;

    try {
      await updateContact({
        token: result.tokenRecupero,
        data: { vuoleEssereRicontattato: wantsContact },
      });
      toast.success(
        wantsContact
          ? t("richiestaPreventivo.results.toastContactYes")
          : t("richiestaPreventivo.results.toastContactNo"),
      );

      // Reset completo
      setShowContactRequest(false);
      setWantsContact(false);
      setResult(null);
      setActiveStep(0);
      reset();
    } catch (error) {
      toast.error(t("richiestaPreventivo.results.toastContactError"));
      console.error(error);
    }
  };

  const handleNewRequest = () => {
    setShowContactRequest(false);
    setWantsContact(false);
    setResult(null);
    setActiveStep(0);
    reset();
  };

  const handleLanguageSelect = (code: string) => {
    i18n.changeLanguage(code);
    setPrevStep(0);
    setActiveStep(1);
  };

  if (result && showContactRequest) {
    return (
      <ResultsView
        result={result}
        tipoRichiesta={tipoRichiesta}
        tipoOlioLavorazioni={tipoOlioLavorazioni}
        wantsContact={wantsContact}
        isUpdatingContact={isUpdatingContact}
        onContactChange={setWantsContact}
        onNewRequest={handleNewRequest}
        onContactSubmit={handleContactSubmit}
      />
    );
  }

  // Calcola il progresso considerando anche la fase finale (submit/risultato)
  // Step 0 = lingua, step 1-3 = form, totalPhases = 3 o 4 step form + 1 risultato
  const currentTotalSteps =
    tipoRichiesta === RichiestaPreventivoTipo.lubrifica ? 3 : 4;
  const totalPhases = currentTotalSteps + 1;

  const displayStep =
    tipoRichiesta === RichiestaPreventivoTipo.lubrifica && activeStep >= 3
      ? activeStep - 1
      : activeStep;
  const progress = (displayStep / totalPhases) * 100;

  // Calcola la direzione per ogni step basandosi sulla navigazione
  const getSlideDirection = (stepIndex: number): "left" | "right" => {
    if (activeStep < prevStep) {
      // Navigazione indietro
      if (stepIndex === activeStep) {
        // Step che entra: arriva da destra
        return "right";
      } else {
        // Step che esce: va verso sinistra
        return "left";
      }
    } else {
      // Navigazione avanti
      if (stepIndex === activeStep) {
        // Step che entra: arriva da sinistra
        return "left";
      } else {
        // Step che esce: va verso destra
        return "right";
      }
    }
  };

  return (
    <CoverLayout bigContent noBackground>
      {/* Language Selector - Top Right */}
      <Box
        sx={{
          position: "fixed",
          top: 3,
          right: 24,
          zIndex: 200,
        }}
      >
        <LanguageSelector />
      </Box>

      {/* Progress Bar - Full Width */}
      <ProgressBar
        activeStep={activeStep}
        displayStep={displayStep}
        currentTotalSteps={currentTotalSteps}
        progress={progress}
      />

      <Container maxWidth="md">
        <Box sx={{ py: 4, pb: 28 }}>
          {/* Title */}
          <Box textAlign="center" mb={4}>
            <Typography
              variant={activeStep === 0 ? "h1" : "h3"}
              sx={{ color: "#333", fontWeight: "bold", fontSize: activeStep === 0 ? { xs: "2.5rem", md: "3.5rem" } : undefined }}
              gutterBottom
            >
              {t("richiestaPreventivo.title")}
            </Typography>
            <Typography variant="body1" sx={{ color: "#666" }}>
              {t("richiestaPreventivo.subtitle")}
            </Typography>
          </Box>

          {/* Form Content */}
          <Box component="form" position="relative">
            <Step0_LanguageSelection
              activeStep={activeStep}
              slideDirection={getSlideDirection(0)}
              onLanguageSelect={handleLanguageSelect}
            />

            <Step1_TipoRichiesta
              activeStep={activeStep}
              slideDirection={getSlideDirection(1)}
              control={control}
            />

            <Step2_TipoFluido
              activeStep={activeStep}
              slideDirection={getSlideDirection(2)}
              control={control}
            />

            <Step3_Dettagli
              activeStep={activeStep}
              slideDirection={getSlideDirection(3)}
              tipoRichiesta={tipoRichiesta}
              tipoOlioLavorazioni={tipoOlioLavorazioni}
              register={register}
              control={control}
              errors={errors}
            />

            <Step4_DatiAzienda
              activeStep={activeStep}
              slideDirection={getSlideDirection(4)}
              register={register}
              errors={errors}
            />
          </Box>
        </Box>
      </Container>

      {/* Fixed Bottom Bar */}
      <BottomNavigation
        activeStep={activeStep}
        isStep1Valid={isStep1Valid}
        isStepFinalValid={isStepFinalValid}
        isCreating={isCreating}
        isUpdatingContact={isUpdatingContact}
        onBack={handleBack}
        onNext={handleNext}
        onSubmit={handleSubmit(onSubmit)}
      />
    </CoverLayout>
  );
}

export default RichiestaPreventivo;
